import React, { useState } from 'react';
import { useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
import { company } from '../data/company';
import { WEB3FORMS_ACCESS_KEY } from '../data/contactForm';
type SubmissionStatus = 'idle' | 'sending' | 'success' | 'error';
type Web3FormsResponse = {
  success?: boolean;
  message?: string;
};
export function ContactPage() {
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [submissionError, setSubmissionError] = useState('');
  const {
    locale
  } = useLocale();
  const t = locale === 'nl' ? {
    eyebrow: 'Contact',
    title: 'Laten we vinden wat u nodig hebt.',
    body: 'Stel uw vraag over producten, beschikbaarheid in de afdelingen, zakelijke behoeften of een offerte.',
    phoneLabel: 'Telefoon',
    emailLabel: 'E-mail',
    addressLabel: 'Adres',
    companyLabel: 'Onderneming',
    enterprise: 'Ondernemingsnummer',
    vat: 'Btw-nummer',
    formLabel: 'Aanvraagformulier',
    name: 'Volledige naam',
    email: 'E-mailadres',
    need: 'Uw vraag',
    placeholder: 'Producten, hoeveelheden of info over een afdeling',
    send: 'Aanvraag versturen',
    sending: 'Versturen…',
    success: 'Bedankt. Ons team antwoordt binnen één werkdag.',
    mailClient: 'Uw e-mailprogramma opent met uw bericht — klik op verzenden om het naar ons te sturen.',
    error: 'Uw aanvraag kon niet worden verzonden. Probeer het opnieuw of mail ons.'
  } : {
    eyebrow: 'Contact',
    title: 'Let’s find what you need.',
    body: 'Ask about products, department availability, business requirements or a quotation.',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    addressLabel: 'Address',
    companyLabel: 'Company',
    enterprise: 'Enterprise number',
    vat: 'VAT number',
    formLabel: 'Enquiry form',
    name: 'Full name',
    email: 'Email address',
    need: 'What do you need?',
    placeholder: 'Products, quantities or department information',
    send: 'Send request',
    sending: 'Sending…',
    success: 'Thank you. Our team will respond within one business day.',
    mailClient: 'Your email app is opening with the message — press send to deliver it to us.',
    error: 'Your request could not be sent. Please try again or email us.'
  };
  async function submitContactForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('sending');
    setSubmissionError('');
    const form = event.currentTarget;
    const formData = new FormData(form);
    const entries = Object.fromEntries(formData.entries()) as Record<string, string>;
    const subject = locale === 'nl' ? `Nieuwe contactaanvraag — ${company.name}` : `New contact enquiry — ${company.name}`;
    const payload = {
      ...entries,
      access_key: WEB3FORMS_ACCESS_KEY,
      subject,
      from_name: company.name,
      replyto: entries.email ?? '',
      language: locale === 'nl' ? 'Nederlands' : 'English'
    };
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result: Web3FormsResponse = await response.json();
      if (!response.ok || !result.success) {
        setSubmissionError(result.message ?? '');
        setStatus('error');
        return;
      }
      form.reset();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }
  const fieldClass = 'mt-3 w-full border-b border-ink/30 bg-transparent pb-2 font-sans text-base outline-none transition focus:border-cobalt';
  return <SiteLayout data-id="element-110" data-mp="18tx26wj5e6zg">
      <main data-id="element-111" data-mp="18uf8klziy1l3">
        <section className="border-b border-ink" data-id="element-112" data-mp="18uoxe68u22p7">
          <div className="mx-auto max-w-[1500px] px-5 py-16 lg:px-10 lg:py-20" data-id="element-113" data-mp="1dsgncupx3eqr">
            <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-114" data-mp="1dubcymc4vv16">
              {t.eyebrow}
            </p>
            <h1 className="mt-8 max-w-[18ch] font-display text-[2.75rem] leading-[0.98] tracking-[-0.03em] sm:text-6xl" data-id="element-115" data-mp="1mtobwl2mt17n">
              {t.title}
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-8 text-ink/70" data-id="element-116" data-mp="1rq5cwfuxja25">
              {t.body}
            </p>
          </div>
        </section>

        <section data-id="element-117" data-mp="21hj8icjrfpq0">
          <div className="mx-auto grid max-w-[1500px] lg:grid-cols-[0.85fr_1.15fr]" data-id="element-118" data-mp="21j43g39zlaoe">
            <div className="border-b border-ink px-5 py-12 lg:border-b-0 lg:border-r lg:px-10 lg:py-16" data-id="element-119" data-mp="25jwcavey5dk5">
              <dl className="border-t border-ink" data-id="element-120" data-mp="2ajsfwtjqf2z6">
                <div className="border-b border-stone py-6" data-id="element-121" data-mp="2agwhz57vweaq">
                  <dt className="font-mono text-[10px] uppercase tracking-label text-ink/45" data-id="element-122" data-mp="2filr2fbjqhw6">
                    {t.phoneLabel}
                  </dt>
                  <dd className="mt-3" data-id="element-123" data-mp="2kmxq1hk7h298">
                    <a href={`tel:${company.phoneHref}`} className="font-display text-xl underline decoration-cobalt decoration-2 underline-offset-4 hover:text-cobalt" data-id="element-124" data-mp="2klwezd36wfri">
                      {company.phone}
                    </a>
                  </dd>
                </div>
                <div className="border-b border-stone py-6" data-id="element-125" data-mp="38e36nyxyzykl">
                  <dt className="font-mono text-[10px] uppercase tracking-label text-ink/45" data-id="element-126" data-mp="33egrhjyl0895">
                    {t.emailLabel}
                  </dt>
                  <dd className="mt-3" data-id="element-127" data-mp="2uku34ewpe79z">
                    <a href={`mailto:${company.email}`} className="font-display text-xl underline decoration-cobalt decoration-2 underline-offset-4 hover:text-cobalt" data-id="element-128" data-mp="2uk2exp5frbtq">
                      {company.email}
                    </a>
                  </dd>
                </div>
                <div className="border-b border-stone py-6" data-id="element-129" data-mp="1kgtyiy2cug9v">
                  <dt className="font-mono text-[10px] uppercase tracking-label text-ink/45" data-id="element-130" data-mp="1pc9b7momm64p">
                    {t.addressLabel}
                  </dt>
                  <dd className="mt-3 leading-7" data-id="element-131" data-mp="1fnrbl8s3252n">
                    {company.name}
                    <br data-id="element-132" data-mp="1fleoyibdr6j9" />
                    {company.street}
                    <br data-id="element-133" data-mp="11n6rm9afm37k" />
                    {company.city}, {company.country}
                  </dd>
                </div>
                <div className="border-b border-stone py-6" data-id="element-134" data-mp="16kzyl4jk718w">
                  <dt className="font-mono text-[10px] uppercase tracking-label text-ink/45" data-id="element-135" data-mp="rqvg3lio1he6">
                    {t.companyLabel}
                  </dt>
                  <dd className="mt-3 space-y-1 text-sm leading-7 text-ink/70" data-id="element-136" data-mp="2m3tp81smq7zx">
                    <p data-id="element-137" data-mp="2m1qoampxuugf">
                      {t.enterprise}: {company.enterpriseNumber}
                    </p>
                    <p data-id="element-138" data-mp="2r0k3cjkvitip">
                      {t.vat}: {company.vatNumber}
                    </p>
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-chalk px-5 py-12 lg:px-12 lg:py-16" data-id="element-139" data-mp="aib0cdenqs4x">
              <h2 className="font-mono text-[10px] uppercase tracking-label text-ink/45" data-id="element-140" data-mp="p6fjtjljb7or">
                {t.formLabel}
              </h2>
              <form onSubmit={submitContactForm} className="mt-8 max-w-xl" data-id="element-141" data-mp="kcktvyfc5wh8">
                <input type="checkbox" name="botcheck" className="hidden" tabIndex={-1} autoComplete="off" data-id="element-142" data-mp="y4hj9upmds9x" />
                <label className="block font-mono text-[10px] uppercase tracking-label text-ink/60" data-id="element-143" data-mp="1825pphwl8o7x">
                  {t.name}
                  <input required name="name" autoComplete="name" className={fieldClass} data-id="element-144" data-mp="39vd32lsn4vr6" />
                </label>
                <label className="mt-9 block font-mono text-[10px] uppercase tracking-label text-ink/60" data-id="element-145" data-mp="2j3w5bqkl8b4r">
                  {t.email}
                  <input required name="email" type="email" autoComplete="email" className={fieldClass} data-id="element-146" data-mp="2o14n3q1tqd5h" />
                </label>
                <label className="mt-9 block font-mono text-[10px] uppercase tracking-label text-ink/60" data-id="element-147" data-mp="204i9t477msc5">
                  {t.need}
                  <textarea required name="message" rows={5} placeholder={t.placeholder} className={`${fieldClass} resize-none placeholder:text-ink/35`} data-id="element-148" data-mp="254mqau84bn2d" />
                </label>
                <button type="submit" disabled={status === 'sending'} className="mt-10 border border-ink bg-ink px-6 py-4 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition hover:border-cobalt hover:bg-cobalt disabled:cursor-not-allowed disabled:opacity-60" data-id="element-149" data-mp="3limpg2z538ok">
                  {status === 'sending' ? t.sending : t.send}
                </button>
                {status === 'success' && <p role="status" className="mt-6 border-l-2 border-cobalt bg-paper py-3 pl-4 text-sm text-ink" data-id="element-150" data-mp="1056luxuyaiq3">
                    {t.success}
                  </p>}
                {status === 'error' && <div role="alert" className="mt-6 border-l-2 border-clay bg-paper py-3 pl-4 text-sm text-ink" data-id="element-151" data-mp="m4ngxzls7ss5">
                    <p data-id="element-152" data-mp="h7x3lk1d3baf">{t.error}</p>
                    {submissionError && <p className="mt-1 text-xs text-ink/60" data-id="element-153" data-mp="2giep0uogq4lh">
                        {submissionError}
                      </p>}
                  </div>}
              </form>
            </div>
          </div>
        </section>
      </main>
    </SiteLayout>;
}